import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import axios from "axios";
import { ContextualLinkPanel } from "@/components/ContextualLinkPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim() || !email.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, {
        email: email.trim(),
        message: message.trim()
      });
      
      setIsSubmitted(true);
      toast.success("Message sent successfully");
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      {/* Main Content */}
      <main className="flex-1 py-16 md:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xl mx-auto">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="font-manrope font-bold text-3xl md:text-4xl text-foreground mb-4">
              Contact Us
            </h1>
            <div className="space-y-3 text-muted-foreground text-base leading-relaxed">
              <p>Have a question? We&apos;d be happy to help.</p>
              <p>Whether you&apos;re looking for clarification on your assessment, your recommendations, or our methodology, send us a message below.</p>
            </div>
          </div>

          {/* Contact Form */}
          {!isSubmitted ? (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="space-y-2">
                <label 
                  htmlFor="message" 
                  className="text-sm font-medium text-foreground"
                >
                  Message
                </label>
                <Textarea
                  id="message"
                  data-testid="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  className="min-h-[160px] resize-none"
                  required
                />
              </div>

              <div className="space-y-2">
                <label 
                  htmlFor="email" 
                  className="text-sm font-medium text-foreground"
                >
                  Email address
                </label>
                <Input
                  id="email"
                  data-testid="contact-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                />
              </div>

              <Button
                type="submit"
                data-testid="contact-submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                {isSubmitting ? "Sending..." : "Send message"}
              </Button>
            </form>
          ) : (
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-8 text-center" data-testid="contact-success">
              <p className="text-foreground font-medium mb-2">Thanks — we received your message.</p>
              <p className="text-muted-foreground">If you included your email, we may follow up.</p>
            </div>
          )}

          {/* Response Expectation */}
          {!isSubmitted && (
            <div className="mt-8 space-y-2">
              <p className="text-sm text-muted-foreground">
                We typically respond within 1–2 business days.
              </p>
              <p className="text-xs text-muted-foreground">
                Looking for a quick answer? Visit our{" "}
                <Link 
                  to="/methodology" 
                  className="text-primary hover:underline"
                >
                  FAQs
                </Link>
                .
              </p>
            </div>
          )}

          <div className="mt-10">
            <ContextualLinkPanel
              kicker="Helpful while you wait"
              title="These pages answer most pre-decision questions quickly."
              description="If your question is really about how the process works, what the paid output looks like, or whether you should start now, these are usually the fastest next stops."
              testIdPrefix="contact-next-steps"
              items={[
                {
                  slug: "faq",
                  eyebrow: "Quick answers",
                  title: "Read the FAQs",
                  description: "Get straight answers about methodology, recommendations, refreshes, and what Relocation Clarity does not do.",
                  ctaLabel: "Open the FAQs",
                  to: "/methodology",
                },
                {
                  slug: "sample-blueprint",
                  eyebrow: "See the product",
                  title: "Preview a Sample Blueprint",
                  description: "See how a paid Blueprint is structured before deciding whether it would be useful for your move.",
                  ctaLabel: "View the sample",
                  to: "/sample-plan",
                },
                {
                  slug: "assessment",
                  eyebrow: "Get started",
                  title: "Start your assessment",
                  description: "Begin the free step if you are ready to move from questions into a shortlist based on your priorities.",
                  ctaLabel: "Begin the assessment",
                  to: "/assessment",
                },
              ]}
            />
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
